import type { BlockPromptContext } from './types.ts';
import { buildProgressionClause, buildExampleClause, buildCrossMonthClause } from './types.ts';
import { buildPersonalizationClause } from './personalization-helpers.ts';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../../constants/languages.ts';

export function buildVocabularyGridPrompt(ctx: BlockPromptContext): string {
  const source = SOURCE_LANGUAGES.find((l) => l.code === ctx.profile.native_language);
  const target = TARGET_LANGUAGES.find((l) => l.code === ctx.profile.target_language);

  const previousVocabSection =
    ctx.previousVocabulary.length > 0
      ? `\n## Previously Introduced Vocabulary (reuse ~30%, but don't repeat the same grid)

${ctx.previousVocabulary.map((v) => `- ${v}`).join('\n')}`
      : '';

  return `Generate a **Vocabulary Grid** exercise block for a ${ctx.profile.level.toUpperCase()} ${target?.name} learner whose native language is ${source?.name}.

## Theme
Workbook theme: "${ctx.workbookTheme}"
Sub-theme for this block: "${ctx.subTheme}"

## Block Position
This is block ${ctx.blockOrder} of ${ctx.totalBlocks}.
${buildPersonalizationClause(ctx.profile, ctx.blockOrder, ctx.isPreview)}${previousVocabSection}

## Requirements

1. Generate **12-14 vocabulary items** related to the sub-theme "${ctx.subTheme}".
2. Each item has a target-language word/phrase and its native-language translation.
3. For each item, set either \`hideTarget: true\` OR \`hideNative: true\` (learner fills in the hidden one). Some items (20-30%) should have both visible (reference rows).
4. At least **40%** of items must have a hidden cell.
5. For nouns in gendered languages, include the \`gender\` field (e.g., "m.", "f.").
6. Include an \`example\` sentence for at least **4** items — a short natural sentence using the word in context.
7. Mix the hiding pattern: alternate between hiding target and hiding native, don't cluster.
8. Instructions should tell the learner to fill in the missing translations **using a dictionary or the internet** to look up unknown words. Mention that the fully completed rows serve as reference examples. Written in ${source?.name}.
9. The \`instructionsNative\` should be a brief version in ${target?.name}, also mentioning dictionary/internet usage.

## Output Format

Return a single JSON object matching this TypeScript interface exactly:

\`\`\`typescript
interface VocabularyGridBlock {
  id: string;              // Generate a UUID v4
  type: "vocabulary_grid"; // Literal
  order: ${ctx.blockOrder};
  title: string;           // In ${target?.name} — the sub-theme as a title
  titleNative: string;     // Same title in ${source?.name} with a short descriptor
  theme: "${ctx.subTheme}";
  estimatedPages: 1.5;
  instructions: string;    // ⚠️ MUST be in ${source?.name} — NEVER in ${target?.name}
  instructionsNative: string; // ⚠️ MUST be in ${target?.name} — brief companion version
  items: Array<{
    target: string;        // Word/phrase in ${target?.name}
    native: string;        // Translation in ${source?.name}
    hideTarget: boolean;
    hideNative: boolean;
    gender?: string;       // "m." or "f." for nouns
    example?: string;      // Example sentence in ${target?.name}
  }>;
}
\`\`\`

Respond with ONLY the JSON object.${buildProgressionClause(ctx)}${buildExampleClause(ctx, 'vocabulary_grid')}${buildCrossMonthClause(ctx)}`;
}
